import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, Edit, Trash2,
  ChevronLeft, ChevronRight, RefreshCw,
  Phone, Mail, MapPin, Users, Globe
} from 'lucide-react';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const navigate = useNavigate();

  // Stats globales
  const [globalStats, setGlobalStats] = useState({
    total_companies: 0,
    active_companies: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    is_active: true,
    max_users: 10
  });

  const itemsPerPage = 10;

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCompanies(data || []);
      
      setGlobalStats({
        total_companies: data?.length || 0,
        active_companies: data?.filter(c => c.is_active).length || 0
      });

    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur chargement', 'error');
    } finally {
      setLoading(false);
    }
  }

  function showNotification(message, type = 'success') {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  }

  async function saveCompany() {
    try {
      if (!formData.name) {
        showNotification('Le nom est requis', 'error');
        return;
      }

      const companyData = {
        name: formData.name,
        code: formData.code || formData.name.substring(0, 3).toUpperCase() + Date.now().toString().slice(-4),
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        address: formData.address || null,
        is_active: formData.is_active,
        max_users: formData.max_users
      };

      if (editingCompany) {
        const { error } = await supabase
          .from('companies')
          .update(companyData)
          .eq('id', editingCompany.id);
        if (error) throw error;
        showNotification('Compagnie modifiée');
      } else {
        const { error } = await supabase
          .from('companies')
          .insert([{ ...companyData, created_at: new Date().toISOString() }]);
        if (error) throw error;
        showNotification('Compagnie créée');
      }

      setShowModal(false);
      resetForm();
      loadCompanies();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification("Erreur: " + error.message, 'error');
    }
  }

  async function deleteCompany(id) {
    if (!window.confirm('Supprimer cette compagnie ?')) return;

    try {
      const { error } = await supabase.from('companies').delete().eq('id', id);
      if (error) throw error;
      showNotification('Compagnie supprimée');
      loadCompanies();
    } catch (error) {
      showNotification('Erreur suppression', 'error');
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      code: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      is_active: true,
      max_users: 10
    });
    setEditingCompany(null);
  }

  const filteredCompanies = companies.filter(c =>
    !searchTerm || 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6 p-6">
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg ${
          notification.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
          Gestion des Compagnies
        </h1>
        <div className="flex gap-2">
          <button onClick={loadCompanies} className="p-2 bg-gray-900 rounded-lg">
            <RefreshCw size={18} className="text-gray-400" />
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-4 py-2 bg-cyan-600 rounded-lg text-white flex items-center gap-2"
          >
            <Plus size={18} />
            Nouvelle compagnie
          </button>
        </div>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 p-4 rounded-xl border border-gray-800">
          <p className="text-sm text-gray-500">Total compagnies</p>
          <p className="text-2xl font-bold text-white">{globalStats.total_companies}</p>
        </div>
        <div className="bg-gray-900 p-4 rounded-xl border border-gray-800">
          <p className="text-sm text-gray-500">Compagnies actives</p>
          <p className="text-2xl font-bold text-green-400">{globalStats.active_companies}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Rechercher par nom, code ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
        />
      </div>

      {/* Liste des compagnies */}
      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : paginatedCompanies.length === 0 ? (
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
          <Building2 size={48} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune compagnie</h3>
          <p className="text-gray-400 mb-4">Créez votre première compagnie</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-4 py-2 bg-cyan-600 rounded-lg text-white"
          >
            <Plus size={18} className="inline mr-2" />
            Nouvelle compagnie
          </button>
        </div>
      ) : (
        paginatedCompanies.map(company => (
          <div 
            key={company.id} 
            className="bg-gray-900 rounded-xl border border-gray-800 p-5 hover:border-cyan-500/50 transition cursor-pointer"
            onClick={() => navigate(`/companies/${company.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium text-lg">{company.name}</h3>
                  {company.code && (
                    <span className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-400">
                      {company.code}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  company.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {company.is_active ? 'Actif' : 'Inactif'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCompany(company);
                    setFormData({
                      name: company.name,
                      code: company.code || '',
                      contact_email: company.contact_email || '',
                      contact_phone: company.contact_phone || '',
                      address: company.address || '',
                      is_active: company.is_active,
                      max_users: company.max_users || 10
                    });
                    setShowModal(true);
                  }}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <Edit size={14} className="text-gray-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCompany(company.id);
                  }}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <Trash2 size={14} className="text-gray-400" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              {company.address && (
                <p className="text-gray-400 flex items-center gap-2">
                  <MapPin size={14} className="text-gray-500" />
                  {company.address}
                </p>
              )}
              {company.contact_email && (
                <p className="text-gray-400 flex items-center gap-2">
                  <Mail size={14} className="text-gray-500" />
                  <a href={`mailto:${company.contact_email}`} className="hover:text-cyan-400">
                    {company.contact_email}
                  </a>
                </p>
              )}
              {company.contact_phone && (
                <p className="text-gray-400 flex items-center gap-2">
                  <Phone size={14} className="text-gray-500" />
                  <a href={`tel:${company.contact_phone}`} className="hover:text-cyan-400">
                    {company.contact_phone}
                  </a>
                </p>
              )}
              <p className="text-gray-500 flex items-center gap-2 text-xs mt-2">
                <Users size={14} />
                Max utilisateurs: {company.max_users || 'Non défini'}
              </p>
            </div>
          </div>
        ))
      )}

      {/* Pagination */}
      {filteredCompanies.length > 0 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Page {currentPage} / {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 bg-gray-800 rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={18} className="text-gray-400" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 bg-gray-800 rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={18} className="text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Modal Compagnie */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              {editingCompany ? 'Modifier' : 'Nouvelle'} compagnie
            </h2>
            <div className="space-y-4">
              <input
                placeholder="Nom *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
              <input
                placeholder="Code (optionnel)"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
              <input
                placeholder="Email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
              <input
                placeholder="Téléphone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
              <textarea
                placeholder="Adresse"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                rows="2"
              />
              <input
                type="number"
                placeholder="Max utilisateurs"
                value={formData.max_users}
                onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value)})}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <span className="text-white">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-800 rounded-lg">
                Annuler
              </button>
              <button onClick={saveCompany} className="px-4 py-2 bg-cyan-600 rounded-lg">
                {editingCompany ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}